import { v1 as uuidv1 } from 'uuid';

/**
 * ForgeRock transaction-id middleware.
 *
 * Generates a single `X-ForgeRock-TransactionId` per browser session and
 * stamps it on every ForgeRock journey request made by the SDK so SRE and
 * ForgeRock Cloud audit logs can correlate every call made by one user in
 * one session (login, verify-device, forgot-password, ...).
 *
 * Matches the cw-identify-web pattern (libs/data-access-forge-rock).
 * See: https://backstage.forgerock.com/docs/am/7.5/audit-guide/audit-log-entries.html
 */

export const APP_TRANSACTION_ID_PREFIX = 'AdvisorOWAUI';
export const FORGEROCK_TRANSACTION_ID_HEADER = 'X-ForgeRock-TransactionId';
export const FORGEROCK_TRANSACTION_ID_COOKIE = 'xftid';
export const FORGEROCK_TRANSACTION_ID_COOKIE_DOMAIN = '.lpl.com';

/**
 * Generate a new ForgeRock transaction id. Uses uuid v1 (time-based) to
 * match the cw-identify-web reference implementation.
 */
export function generateForgeRockTransactionId(): string {
  return `${APP_TRANSACTION_ID_PREFIX}-${uuidv1()}`;
}

/**
 * Module-scoped singleton: one transaction id for the entire app session.
 * Computed exactly once at import time so it stays stable across login,
 * verify-device and forgot-password journeys.
 */
export const forgeRockTransactionId: string = generateForgeRockTransactionId();

interface ForgeRockMiddlewareRequest {
  init: { headers?: unknown };
}

interface ForgeRockMiddlewareAction {
  type: string;
}

type ForgeRockMiddleware = (
  req: ForgeRockMiddlewareRequest,
  action: ForgeRockMiddlewareAction,
  next: () => void,
) => void;

/**
 * Factory for the ForgeRock SDK middleware that stamps the
 * `X-ForgeRock-TransactionId` header on journey requests and writes the
 * `xftid` cookie on journey start.
 *
 * Only `START_AUTHENTICATE` and `AUTHENTICATE` actions are tagged — matches
 * the cw-identify-web reference. Other action types (token exchange, logout,
 * user-info, ...) are left untouched.
 *
 * @param transactionId - override the session id (primarily for tests).
 */
export function createForgeRockTransactionIdMiddleware(
  transactionId: string = forgeRockTransactionId,
): ForgeRockMiddleware {
  return (req, action, next) => {
    const headers = req.init.headers as Record<string, string>;

    if (action.type === 'START_AUTHENTICATE') {
      headers[FORGEROCK_TRANSACTION_ID_HEADER] = transactionId;
      document.cookie =
        `${FORGEROCK_TRANSACTION_ID_COOKIE}=${transactionId};` +
        ` path=/; domain=${FORGEROCK_TRANSACTION_ID_COOKIE_DOMAIN};` +
        ` secure; SameSite=None`;
    }
    if (action.type === 'AUTHENTICATE') {
      headers[FORGEROCK_TRANSACTION_ID_HEADER] = transactionId;
    }

    next();
  };
}
